/**
 * The languages the user guide is written in. The codes are BCP 47 like every locale in Glossa
 * (German for Austria, Serbian in Latin script); Help Admin narrows them to the primary subtag.
 */
export const HELP_LANGUAGES = ['en', 'de-AT', 'sr-Latn-RS'] as const;
export type HelpLanguage = (typeof HELP_LANGUAGES)[number];
export const HELP_BASE_LANGUAGE: HelpLanguage = 'en';

/** The short label of a language on the user menu's toggle. */
export const HELP_LANGUAGE_LABELS: Record<HelpLanguage, string> = {
  en: 'EN',
  'de-AT': 'DE',
  'sr-Latn-RS': 'SR',
};

const STORAGE_PREFIX = 'glossa.help';

/**
 * Which language the user guide answers in, picked in the user menu.
 *
 * One control's own setting rather than app state, kept in localStorage so a user does not
 * re-pick their language on every visit (the key predates the Help Admin sidebar, so earlier
 * choices carry over). Validated on read against the language list, so a stale value cannot
 * select a language the help was never written in.
 */
export const HELP_LANGUAGE_KEY = `${STORAGE_PREFIX}.language`;

export const readLanguagePref = (): HelpLanguage => {
  try {
    const stored = window.localStorage.getItem(HELP_LANGUAGE_KEY);
    const match = HELP_LANGUAGES.find((code) => code === stored);
    return match ?? HELP_BASE_LANGUAGE;
  } catch {
    // Storage can throw outright (private mode, blocked site data) — fall back to the default.
    return HELP_BASE_LANGUAGE;
  }
};

/** Fired on `window` when the language changes in this tab; `storage` covers the other tabs. */
export const HELP_LANGUAGE_EVENT = 'glossa-help-language';

export const writeLanguagePref = (language: HelpLanguage): void => {
  try {
    window.localStorage.setItem(HELP_LANGUAGE_KEY, language);
  } catch {
    // Best-effort: the choice then lasts for this session only.
  }
  window.dispatchEvent(new Event(HELP_LANGUAGE_EVENT));
};
